import { EventNoteOutlined, FlagOutlined, PlaceOutlined, ViewQuiltOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { ElementType, FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axios';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';

export type IdsMappingKind = 'event' | 'venue' | 'layout' | 'status';

interface IdsMappingKpiCardsProps {
  currentType?: IdsMappingKind;
  refreshKey?: number;
}

interface StatCardData {
  key: IdsMappingKind;
  label: string;
  value: number;
  subtitle: string;
  icon: ElementType;
  iconColor: string;
  iconBgColor: string;
  path: string;
}

const StatCard: FC<{ stat: StatCardData; selected?: boolean; onClick: () => void }> = ({ stat, selected, onClick }) => {
  const IconComponent = stat.icon;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        minHeight: 72,
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        border: selected ? '1.5px solid #1E3A8A' : '1px solid #E5E7EB',
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          borderColor: '#1E3A8A',
          boxShadow: '0 1px 4px rgba(16, 24, 40, 0.08)',
        },
      }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: stat.iconBgColor,
          color: stat.iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
        <IconComponent sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.15 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'text.secondary',
            lineHeight: 1.2,
          }}>
          {stat.label}
        </Typography>
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: '#101828',
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
          }}>
          {stat.value}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 400,
            color: 'text.secondary',
            lineHeight: 1.2,
          }}>
          {stat.subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

const IdsMappingKpiCards: FC<IdsMappingKpiCardsProps> = ({ currentType, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ event: 0, venue: 0, layout: 0, status: 0 });

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.getAllIdsMappings);
      setCounts({
        event: data?.eventMappings?.length || 0,
        venue: data?.venueMappings?.length || 0,
        layout: data?.layoutMappings?.length || 0,
        status: data?.statusMappings?.length || 0,
      });
    } catch {
      // Keep last known counts if the overview request fails.
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, refreshKey]);

  const stats: StatCardData[] = [
    {
      key: 'event',
      label: 'Event Mappings',
      value: counts.event,
      subtitle: 'Mapped event types',
      icon: EventNoteOutlined,
      iconColor: '#1E3A8A',
      iconBgColor: '#E8EEF7',
      path: routes.eventMappings,
    },
    {
      key: 'venue',
      label: 'Venue Mappings',
      value: counts.venue,
      subtitle: 'Mapped venues',
      icon: PlaceOutlined,
      iconColor: '#188038',
      iconBgColor: '#E6F4EA',
      path: routes.venueMappings,
    },
    {
      key: 'layout',
      label: 'Layout Mappings',
      value: counts.layout,
      subtitle: 'Mapped layouts',
      icon: ViewQuiltOutlined,
      iconColor: '#D97706',
      iconBgColor: '#FFFBEB',
      path: routes.layoutMappings,
    },
    {
      key: 'status',
      label: 'Status Mappings',
      value: counts.status,
      subtitle: 'Mapped booking statuses',
      icon: FlagOutlined,
      iconColor: '#4338CA',
      iconBgColor: '#EEF2FF',
      path: routes.statusMappings,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 1.25,
        width: '100%',
      }}>
      {stats.map(stat => (
        <StatCard
          key={stat.key}
          stat={stat}
          selected={currentType === stat.key}
          onClick={() => {
            if (currentType !== stat.key) navigate(stat.path);
          }}
        />
      ))}
    </Box>
  );
};

export default IdsMappingKpiCards;
